import { Hidden } from '@material-ui/core'
import React, { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import styled from 'styled-components'

import { set_la_api_token } from '../../helpers/cookies'
import routes from '../../routes'
import { Box, Text } from '../Shared/Base'
import { FormElementGlobalError } from '../Shared/Form/Element'
import { FocusableField, useFormState } from '../Shared/Form/Field'
import { FormPasswordInput, FormTextFieldInput } from '../Shared/Form/Input'
import { valid_email, valid_required } from '../Shared/Form/validators'
import { useCheckAuthentication, useFetchApi, useSignRedirect } from '../Shared/hooks'
import LinkSafe from '../Shared/LinkSafe'
import { SignAppleButton } from './AppleButton'
import { SignGoogleButton } from './GoogleButton'

const RightStyled = styled(Box)`
  background: url('https://res.cloudinary.com/loueragile/image/upload/f_auto/v1/web/photos/signup.jpg');
  background-size: cover;
  background-repeat: no-repeat;
`

const form_validators = {
  password: { required: valid_required, length: v => v.length > 5 },
  email: { required: valid_required, is_email: valid_email }
}
const form_initial_values = { password: '', email: '' }

const SignIn = () => {
  useEffect(() => {
    if (navigator.userAgent !== 'ReactSnap') {
      window.location.reload()
    }
  }, [])

  const [oauth_error, set_oauth_error] = React.useState(null)
  const location_search = useLocation().search
  const fetcher = useFetchApi()
  const check_authentication = useCheckAuthentication()
  const sign_redirect = useSignRedirect()

  const onSubmitSuccess = React.useCallback(
    async values => {
      const { access_token } = await fetcher('user/auth', {
        method: 'POST',
        body: JSON.stringify({ email: values.email, password: values.password })
      })
      set_la_api_token(access_token)
      await check_authentication()
      sign_redirect()
    },
    [check_authentication, fetcher, sign_redirect]
  )

  const form = useFormState({
    initial_values: form_initial_values,
    validators: form_validators,
    onSubmitSuccess
  })

  const onOauthSuccess = React.useCallback(
    async ({ oauth_url, oauth_body }) => {
      try {
        const { access_token } = await fetcher(oauth_url, {
          method: 'POST',
          body: JSON.stringify(oauth_body)
        })
        set_la_api_token(access_token)
        await check_authentication()
        sign_redirect()
      } catch (err) {
        if (err.name === 'FetchError' && err.status === 400) {
          set_oauth_error(err.errorMessage)
        } else {
          throw err
        }
      }
    },
    [check_authentication, fetcher, sign_redirect]
  )

  return (
    <Box display="flex" width="100%" minHeight="100vh">
      <Box width={{ md: '65%', xs: '100%' }} m="auto">
        <Box maxWidth={450} m="auto" px={20} py={20}>
          <Box width="100%" textAlign="center">
            <Box as={LinkSafe} to={routes.homepage.route.reverse()} display="block" mb={30}>
              <Box
                as="img"
                src="https://res.cloudinary.com/loueragile/image/upload/v1653311807/web/jinka/Logo-Jinka-a.svg"
                width={129}
                alt="Logo - Jinka"
                position={{ md: 'absolute', xs: 'relative' }}
                top={{ md: 50, xs: 'auto' }}
                left={{ md: 50, xs: 'auto' }}
                ml={{ md: 0, xs: 'auto' }}
              />
            </Box>
            <Box
              backgroundColor="bg_n_tertiary"
              color="n_tertiary"
              py={16}
              px={30}
              border="1px solid"
              borderColor="n_tertiary"
              mb={30}
              borderRadius={10}
              fontSize={18}
              lineHeight="2.6rem"
            >
              Vous êtes un professionnel ? <br />{' '}
              <strong>
                <Box
                  as="a"
                  href="https://pro.jinka.fr/auth/signin"
                  color="n_tertiary"
                  className="tdu"
                >
                  Connectez-vous à votre espace pro.
                </Box>
              </strong>
            </Box>
            <Box
              as="p"
              className="h2-like"
              mb={30}
              px={20}
              textAlign="center"
              display="inline-block"
            >
              Connexion
            </Box>
          </Box>
          <FormElementGlobalError globalError={form.globalError || oauth_error} />
          <Box>
            {/* <SignGoogleButton
              onSuccess={onOauthSuccess}
              oauth_url={'user/google_signin'}
              label="Connexion avec Google"
            /> */}
            <SignGoogleButton onSuccess={onOauthSuccess} oauth_url={'user/google_signin'} />
            <SignAppleButton
              onSuccess={onOauthSuccess}
              oauth_url={'user/apple_signin'}
              label="Connexion avec Apple"
            />
          </Box>
          <Box my={30} textAlign="center" color="light">
            Ou se connecter par e-mail
          </Box>
          <Box as="form" onSubmit={form.onSubmit} onChange={form.onChange} width="100%">
            <Box>
              <FocusableField
                showRequiredError={form.submittedOnce}
                name="email"
                type="email"
                placeholder="E-mail"
                value={form.values.email}
                validators={form.validators.email}
              >
                {FormTextFieldInput}
              </FocusableField>
            </Box>
            <Box mt={10}>
              <FocusableField
                showRequiredError={form.submittedOnce}
                name="password"
                placeholder="Mot de passe"
                value={form.values.password}
                validators={form.validators.password}
                errorLabels={{
                  length: 'Au moins 6 caractères'
                }}
              >
                {FormPasswordInput}
              </FocusableField>
            </Box>

            <Box mt={10}>
              <Link
                to={{
                  pathname: routes.sign.route.reverse({ action: 'password-forgot' }),
                  search: location_search
                }}
              >
                <Text color="n_tertiary" as="span" className="tdh">
                  Mot de passe oublié
                </Text>
              </Link>
            </Box>

            <Box
              as="button"
              className="btn is-n_secondary is-medium"
              mt={40}
              width="100%"
              disabled={form.loading}
            >
              Se connecter
            </Box>

            {/* <Box mt={20} textAlign="center">
              Pas encore de compte ?{' '}
              <Link
                to={{
                  pathname: routes.sign.route.reverse({ action: 'up' }),
                  search: location_search
                }}
              >
                <Text color="n_tertiary" as="span" className="tdh">
                  S'inscrire
                </Text>
              </Link>
            </Box> */}
          </Box>
        </Box>
      </Box>
      <Hidden smDown>
        <RightStyled width="35%" display="flex" textAlign="center" color="white" px={20} py={20} />
      </Hidden>
    </Box>
  )
}

export default SignIn
